import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../../components/Team/people.css";
import "./public-onboarding.css";
import usePublicPageScroll from "./usePublicPageScroll";
import { getSelfOnboarding, completeSelfStep } from "../../api/onboardingApi";

/* /onboarding/<token> — the employee's checklist on their phone. No login: the
   unguessable token on the assignment is the auth (lane D's contract). Each
   step completes as a real event: a file upload, a typed-name signature on the
   published SOP shown inline, a training confirmation. */

/* Name the cause. A silent "something went wrong" on an employee's phone is how
   two completed steps ended up saving nothing with nobody able to say why. */
const explain = (e) => {
  const status = e?.response?.status ?? e?.status ?? 0;
  const d = e?.response?.data || e?.payload || {};
  const detail = d.detail || d.error || '';
  if (!e?.response && /network|timeout/i.test(String(e?.message || ''))) {
    return 'Your phone lost the connection before the upload finished. Check your signal and try again — nothing was saved.';
  }
  if (status === 404) return 'This link isn’t valid any more. Ask your manager to send a new one.';
  if (status === 413) return 'That file is too large to upload. Take the photo again at a smaller size, or send it to your manager.';
  if (status === 502 || d.error === 'storage_failed') return 'The file reached us but couldn’t be stored. Try again in a minute — nothing was saved.';
  if (status === 409) return detail || 'That step can’t be completed right now.';
  if (status === 400) return detail || 'Something about that file wasn’t accepted. Try a photo or a PDF.';
  return `${detail || 'Something went wrong and nothing was saved.'}${status ? ` (error ${status})` : ''} Try again, or ask your manager.`;
};

const MAX_UPLOAD_MB = 20;

export default function OnboardingSelf() {
  usePublicPageScroll();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const r = await getSelfOnboarding(token); setData(r.data); setError(null); return r.data; }
    catch (e) { setError(explain(e)); return null; }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const complete = async (step, form) => {
    setBusy(step.id);
    setError(null);
    try {
      await completeSelfStep(token, step.id, form);
      // Trust the server, not the click: re-read and confirm this step came back
      // done, rather than leaving the employee thinking it saved when it didn't.
      const fresh = await load();
      const now = (fresh?.steps || []).find((s) => s.id === step.id);
      if (fresh && (!now || now.status !== 'done')) {
        setError('That didn’t save. Try it once more, or ask your manager.');
      }
    } catch (e) { setError(explain(e)); }
    finally { setBusy(null); }
  };

  if (error && !data) return <div className="pub"><div className="pub-wrap"><div className="pub-err">{error}</div></div></div>;
  if (!data) return <div className="pub"><div className="pub-wrap"><div className="pub-sub">Loading your checklist…</div></div></div>;

  const steps = data.steps || [];
  const done = steps.filter((s) => s.status === "done").length;
  const allDone = steps.length > 0 && done === steps.length;
  const first = (data.employee?.name || "").split(" ")[0];

  return (
    <div className="pub">
      <div className="pub-wrap">
        <div>
          <div className="pub-biz">{data.tenant?.name || "Onboarding"}</div>
          <h1>{first ? `${first}, welcome aboard.` : "Welcome aboard."}</h1>
          <div className="pub-sub">{allDone ? "Everything on your list is done. Thank you." : "A few things to finish before your first day. Each one takes a minute from your phone."}</div>
        </div>
        <div className="pub-progress"><div className={`ppl-bar ${allDone ? "is-done" : ""}`}><span style={{ width: `${steps.length ? Math.round((done / steps.length) * 100) : 0}%` }} /></div><span>{done}/{steps.length}</span></div>
        {error && <div className="pub-err">{error}</div>}
        {steps.map((s, i) => <Step key={s.id} n={i + 1} step={s} busy={busy === s.id} onComplete={(form) => complete(s, form)} />)}
        <div className="pub-foot">Questions? Your manager can see this list and help.</div>
      </div>
    </div>
  );
}

function Step({ n, step, busy, onComplete }) {
  const done = step.status === "done";
  return (
    <section className={`pub-card ${done ? "is-done" : ""}`}>
      <div className="pub-card-head">
        <span className="pub-card-title">{n}. {step.label}</span>
        {done ? <span className="pub-done">✓ Done</span> : <span className="pub-todo">To do</span>}
      </div>
      {!done && step.kind === "document_upload" && <Upload hint={step.doc_type_label} busy={busy} onFile={(file) => { const form = new FormData(); form.append("file", file); form.append("platform", "mobile"); onComplete(form); }} />}
      {!done && step.kind === "signature" && <Sign step={step} busy={busy} onSign={(typed) => { const form = new FormData(); form.append("typed_name", typed); form.append("method", "typed_name"); form.append("platform", "mobile"); onComplete(form); }} />}
      {!done && step.kind === "training" && (
        <>
          <div className="pub-card-hint">Confirm once you’ve completed {step.config?.training_course || "this training"}.</div>
          <button type="button" className="pub-btn" disabled={busy} onClick={() => { const form = new FormData(); form.append("course", step.config?.training_course || step.label); form.append("completed_on", new Date().toISOString().slice(0, 10)); form.append("platform", "mobile"); onComplete(form); }}>{busy ? "Saving…" : "I’ve completed this"}</button>
        </>
      )}
      {!done && step.kind === "credential" && <div className="pub-card-hint">Your manager adds this from a photo or PDF of your license. Bring it on your first day, or send it to them now.</div>}
      {done && step.artifact?.type === "file" && <div className="pub-card-hint">Uploaded {step.artifact.name}.</div>}
      {done && step.artifact?.type === "signature" && <div className="pub-card-hint">Signed{step.artifact.typed_name ? ` as ${step.artifact.typed_name}` : ""}.</div>}
    </section>
  );
}

/* ONE TAP. Picking the file sends it.

   The old flow needed two taps — choose a file, then press Upload — and the
   logs show exactly what that costs: on the founder's link, seven GETs and
   zero POSTs over a week. Two steps he believed he had completed never made a
   request at all. Picking a file now uploads it, so there is no second tap to
   miss and no disabled primary button that looks like the action. */
function Upload({ hint, busy, onFile }) {
  const [file, setFile] = useState(null);
  const mb = file ? file.size / (1024 * 1024) : 0;
  const tooBig = mb > MAX_UPLOAD_MB;
  const choose = (picked) => {
    setFile(picked);
    if (picked && picked.size <= MAX_UPLOAD_MB * 1024 * 1024) onFile(picked);
  };
  return (
    <div className="pub-file">
      <div className="pub-card-hint">{hint ? `Take a clear photo of your ${hint}, or pick the file.` : "Take a clear photo, or pick the file."} PDF or image. It sends as soon as you pick it.</div>
      {/* No `capture` attribute: on a phone it forces the camera and hides the
          files/photo-library option, so an employee with a PDF is stuck. */}
      <label className={`pub-btn ${busy ? 'secondary' : ''}`} aria-busy={busy}>
        {busy ? 'Uploading…' : file ? 'Choose a different file' : 'Take a photo / choose a file'}
        <input type="file" accept="image/*,.pdf,application/pdf" hidden disabled={busy} onChange={(e) => { choose(e.target.files?.[0] || null); e.target.value = ''; }} />
      </label>
      {file && <div className="pub-file-name">{file.name} · {mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(file.size / 1024))} KB`}</div>}
      {tooBig && <div className="pub-card-hint">That file is over {MAX_UPLOAD_MB} MB, so it wasn’t sent. Take the photo again at a smaller size, or pick a PDF.</div>}
    </div>
  );
}

function Sign({ step, busy, onSign }) {
  const [typed, setTyped] = useState("");
  const [agree, setAgree] = useState(false);
  const sop = step.sop;
  return (
    <>
      {sop ? (
        <>
          <div className="pub-card-hint">Read <strong>{sop.title}</strong> (version {sop.version}), then sign by typing your full name.</div>
          <div className="pub-doc"><ReactMarkdown remarkPlugins={[remarkGfm]}>{sop.body || ""}</ReactMarkdown></div>
        </>
      ) : (
        <div className="pub-note">There is nothing published to sign here yet. Your manager needs to publish the document first; check back later.</div>
      )}
      {sop && (
        <>
          <label className="pub-check"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />I have read and understood {sop.title}, and I agree to follow it.</label>
          <input className="pub-input" type="text" placeholder="Type your full name to sign" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="name" />
          <button type="button" className="pub-btn" disabled={!agree || typed.trim().length < 3 || busy} onClick={() => onSign(typed.trim())}>{busy ? "Signing…" : "Sign"}</button>
          <div className="pub-card-hint">Your typed name, the time and this device are recorded as your signature.</div>
        </>
      )}
    </>
  );
}
